/**
 * Keeps an open page on the version of the front that the server deploys.
 *
 * Front and backend are deployed together, but a page opened before a deployment (a computer left on overnight)
 * kept running the previous front against the new backend, whose contract may have changed. Three signals tell the
 * page that another version is deployed:
 * - index.html carries another version (read every few minutes and when the page is shown again);
 * - the backend refuses a request of this version (FRONT_VERSION_OUTDATED, FrontVersionInterceptor): nothing it asked
 *   for was executed;
 * - a file of this version is gone from the server (vite:preloadError).
 *
 * The page then reloads itself, at the same address, as soon as it can do it without losing anything: hidden, left
 * idle, at the next navigation, or when a request is refused. Never while something typed is not saved: until then a
 * banner (FrontUpdateBanner) offers to reload, and no error is ever shown.
 */

export const FRONT_VERSION_HEADER = "X-Front-Version"
export const FRONT_VERSION_OUTDATED_ERROR_CODE = "FRONT_VERSION_OUTDATED"

export const SERVED_VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000
export const IDLE_BEFORE_RELOAD_MS = 10 * 60 * 1000
/** A page shown again after being left idle may still reload during this time: its user has not started working. */
export const RETURN_GRACE_MS = 30 * 1000
/** A page loaded by an update reload less than this ago, and still not up to date, stops checking: no reload loop. */
export const RELOAD_LOOP_WINDOW_MS = 2 * 60 * 1000

const RELOADED_AT_KEY = "front-update.reloaded-at"
const NOTICE_KEY = "front-update.notice"

export enum FrontUpdateNotice {
  UPDATED = "UPDATED",
  LAST_ACTION_NOT_SAVED = "LAST_ACTION_NOT_SAVED",
}

export type RefusedRequestOutcome = "RELOADING" | "WAITING_FOR_THE_USER" | "RETRY_WITHOUT_VERSION"

export interface FrontUpdateBannerState {
  visible: boolean
  /** A change the user asked to save was refused: it is still on the screen, not saved. */
  lastChangeNotSaved: boolean
}

export interface FrontUpdateEnvironment {
  /** The version of this front (import.meta.env.VITE_APP_VERSION), undefined when unknown. */
  ownVersion: string | undefined
  now(): number
  /** The tab's sessionStorage: it carries the reload time and the notice to the next page. */
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | undefined
  isPageHidden(): boolean
  reload(): void
}

export interface FrontUpdate {
  /** The value of FRONT_VERSION_HEADER, undefined when this page does not ask the backend to check its version. */
  versionHeaderValue(): string | undefined
  servedVersionRead(servedVersion: string | undefined): void
  requestRefused(write: boolean): RefusedRequestOutcome
  /** A file of this version is gone; true when the page reloads (the import error must then not be thrown). */
  fileOfThisVersionMissing(): boolean
  userInteracted(): void
  inputEdited(): void
  writeSaved(): void
  navigated(): void
  pageShown(): void
  /** Reloads when an update waits and the page can now do it without losing anything. */
  evaluate(): void
  reloadNow(): void
  bannerState(): FrontUpdateBannerState
  subscribe(listener: () => void): () => void
  /** The notice left by the update reload that loaded this page, read once. */
  takeNoticeOfTheReload(): FrontUpdateNotice | undefined
}

const HIDDEN_BANNER: FrontUpdateBannerState = { visible: false, lastChangeNotSaved: false }

function readStorage(storage: FrontUpdateEnvironment["storage"], key: string): string | null {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeStorage(storage: FrontUpdateEnvironment["storage"], key: string, value: string | undefined): void {
  try {
    if (value === undefined) {
      storage?.removeItem(key)
    } else {
      storage?.setItem(key, value)
    }
  } catch {
    // A tab without storage still reloads, it only loses the notice and the loop guard
  }
}

export function createFrontUpdate(environment: FrontUpdateEnvironment): FrontUpdate {
  const { ownVersion, storage } = environment
  const previousUpdateReloadAt = Number(readStorage(storage, RELOADED_AT_KEY)) || undefined
  const listeners = new Set<() => void>()

  let pending = false
  let reloading = false
  // In memory only: a reload by the user checks again, at most one automatic reload follows it
  let disabled = false
  let unsavedInput = false
  let lastChangeNotSaved = false
  let lastInteractionAt = environment.now()
  let returnedAfterIdleAt: number | undefined
  let banner = HIDDEN_BANNER

  function notify(): void {
    const visible = pending && !reloading
    if (banner.visible !== visible || banner.lastChangeNotSaved !== lastChangeNotSaved) {
      banner = visible ? { visible, lastChangeNotSaved } : HIDDEN_BANNER
      listeners.forEach((listener) => listener())
    }
  }

  function loadedByARecentUpdateReload(): boolean {
    return previousUpdateReloadAt !== undefined && environment.now() - previousUpdateReloadAt < RELOAD_LOOP_WINDOW_MS
  }

  /** False when the update must be ignored: checks disabled, or the reload that loaded this page did not help. */
  function updateAvailable(): boolean {
    if (disabled) {
      return false
    }
    if (!pending && loadedByARecentUpdateReload()) {
      // The server still serves or expects another version right after a reload: reloading again would not help
      console.error("Front update: still not up to date after a reload, version checks stopped for this page")
      disabled = true
      return false
    }
    pending = true
    notify()
    return true
  }

  function isIdle(): boolean {
    const now = environment.now()
    return (
      now - lastInteractionAt >= IDLE_BEFORE_RELOAD_MS ||
      (returnedAfterIdleAt !== undefined && now - returnedAfterIdleAt < RETURN_GRACE_MS)
    )
  }

  function canReloadWithoutLoss(): boolean {
    return !unsavedInput && (environment.isPageHidden() || isIdle())
  }

  function reloadForUpdate(notice: FrontUpdateNotice): void {
    reloading = true
    writeStorage(storage, RELOADED_AT_KEY, String(environment.now()))
    writeStorage(storage, NOTICE_KEY, notice)
    notify()
    environment.reload()
  }

  function evaluate(): void {
    if (pending && !reloading && canReloadWithoutLoss()) {
      reloadForUpdate(FrontUpdateNotice.UPDATED)
    }
  }

  return {
    versionHeaderValue: () => (disabled ? undefined : ownVersion),

    servedVersionRead(servedVersion) {
      if (reloading || !servedVersion || !ownVersion || servedVersion === ownVersion) {
        return
      }
      if (updateAvailable()) {
        evaluate()
      }
    },

    requestRefused(write) {
      if (reloading) {
        return "RELOADING"
      }
      if (!updateAvailable()) {
        return "RETRY_WITHOUT_VERSION"
      }
      if (unsavedInput) {
        lastChangeNotSaved = lastChangeNotSaved || write
        notify()
        return "WAITING_FOR_THE_USER"
      }
      // The user has just acted: nothing typed is lost, at worst one click to do again
      reloadForUpdate(write ? FrontUpdateNotice.LAST_ACTION_NOT_SAVED : FrontUpdateNotice.UPDATED)
      return "RELOADING"
    },

    fileOfThisVersionMissing() {
      if (reloading) {
        return true
      }
      if (!updateAvailable()) {
        return false
      }
      // The page cannot show what was asked without the files of the new version
      reloadForUpdate(FrontUpdateNotice.UPDATED)
      return true
    },

    userInteracted() {
      lastInteractionAt = environment.now()
    },

    inputEdited() {
      unsavedInput = true
    },

    writeSaved() {
      unsavedInput = false
    },

    navigated() {
      unsavedInput = false
      if (pending && !reloading) {
        // Leaving the page anyway: the next one is loaded in the new version
        reloadForUpdate(FrontUpdateNotice.UPDATED)
      }
    },

    pageShown() {
      if (environment.now() - lastInteractionAt >= IDLE_BEFORE_RELOAD_MS) {
        returnedAfterIdleAt = environment.now()
      }
      evaluate()
    },

    evaluate,

    reloadNow() {
      reloadForUpdate(FrontUpdateNotice.UPDATED)
    },

    bannerState: () => banner,

    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },

    takeNoticeOfTheReload() {
      const notice = readStorage(storage, NOTICE_KEY)
      writeStorage(storage, NOTICE_KEY, undefined)
      return Object.values(FrontUpdateNotice).find((value) => value === notice)
    },
  }
}

/** What the page loaded by an update reload tells its user. */
export function frontUpdateNoticeMessage(notice: FrontUpdateNotice, version: string | undefined): string {
  switch (notice) {
    case FrontUpdateNotice.LAST_ACTION_NOT_SAVED:
      return "Time To Beem a été mis à jour : votre dernière action n'a pas été prise en compte, refaites-la."
    case FrontUpdateNotice.UPDATED:
    default:
      return version ? `Time To Beem a été mis à jour (version ${version}).` : "Time To Beem a été mis à jour."
  }
}

/** The version that index.html announces, undefined for the development server (its placeholder is not replaced). */
export function servedVersionOf(indexHtml: string): string | undefined {
  const tag = /<meta\b[^>]*\bname="time-to-beem-version"[^>]*>/i.exec(indexHtml)?.[0]
  const version = tag ? /\bcontent="([^"]*)"/i.exec(tag)?.[1]?.trim() : undefined
  return version && !version.includes("__VERSION__") ? version : undefined
}

/** True for the refusal of FrontVersionInterceptor: the backend runs another version than this page. */
export async function isOutdatedFrontRefusal(response: Response): Promise<boolean> {
  if (response.status !== 409) {
    return false
  }
  const body: { message?: string } | undefined = await response
    .clone()
    .json()
    .catch(() => undefined)
  return body?.message === FRONT_VERSION_OUTDATED_ERROR_CODE
}

export function isWriteRequest(method: string | undefined): boolean {
  const upperCaseMethod = (method ?? "GET").toUpperCase()
  return upperCaseMethod !== "GET" && upperCaseMethod !== "HEAD"
}

const NOT_TYPED_INPUT_TYPES = new Set(["button", "submit", "reset", "image"])

/** A field whose value the user changes: typed into it, ticked it, chose a file in it. */
export function isEditableField(target: EventTarget | null): boolean {
  const element = target as { tagName?: string; type?: string; isContentEditable?: boolean } | null
  if (!element) {
    return false
  }
  if (element.isContentEditable) {
    return true
  }
  switch (element.tagName) {
    case "TEXTAREA":
    case "SELECT":
      return true
    case "INPUT":
      return !NOT_TYPED_INPUT_TYPES.has((element.type ?? "text").toLowerCase())
    default:
      return false
  }
}

function browserSessionStorage(): FrontUpdateEnvironment["storage"] {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage
  } catch {
    return undefined
  }
}

export const frontUpdate: FrontUpdate = createFrontUpdate({
  ownVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) || undefined,
  now: () => Date.now(),
  storage: browserSessionStorage(),
  isPageHidden: () => typeof document !== "undefined" && document.visibilityState === "hidden",
  reload: () => window.location.reload(),
})
